"use client"

import * as React from "react"
import * as TooltipPrimitive from "@radix-ui/react-tooltip"

import { cn } from "@/lib/utils"

function TooltipProvider({
  delayDuration = 0,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Provider>) {
  return (
    <TooltipPrimitive.Provider
      data-slot="tooltip-provider"
      delayDuration={delayDuration}
      {...props}
    />
  )
}

function Tooltip({
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Root>) {
  return (
    <TooltipProvider>
      <TooltipPrimitive.Root data-slot="tooltip" {...props} />
    </TooltipProvider>
  )
}

function TooltipTrigger({
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Trigger>) {
  return <TooltipPrimitive.Trigger data-slot="tooltip-trigger" {...props} />
}

function TooltipContent({
  className,
  sideOffset = 0,
  children,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Content>) {
  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        data-slot="tooltip-content"
        sideOffset={sideOffset}
        className={cn(
          // Grain: ink surface + on-ink (paper) text — both tokens invert per
          // mode, so contrast holds in light AND dark. Radius/size per the grain
          // tooltip recipe. Enter scales from .97 + fades (~125ms on --dash-ease,
          // set via the scoped rule in dashboard-themes.css); exit is faster.
          "z-50 w-fit origin-(--radix-tooltip-content-transform-origin) rounded-[8px] px-2.5 py-1.5 text-[11.5px] leading-snug text-balance",
          "bg-[var(--dash-ink,var(--text-high-emphasis))] text-[color:var(--dash-paper,var(--structural-white))]",
          "animate-in fade-in-0 data-[state=closed]:animate-out data-[state=closed]:fade-out-0",
          "duration-[125ms] data-[state=closed]:duration-[100ms]",
          // Movement/scale only when motion is allowed — reduced motion keeps the fade.
          "motion-safe:zoom-in-[.97] motion-safe:data-[state=closed]:zoom-out-[.97]",
          "motion-safe:data-[side=bottom]:slide-in-from-top-1 motion-safe:data-[side=left]:slide-in-from-right-1 motion-safe:data-[side=right]:slide-in-from-left-1 motion-safe:data-[side=top]:slide-in-from-bottom-1",
          className
        )}
        {...props}
      >
        {children}
        {/* Radix renders the arrow as an SVG triangle it centers on the trigger
            using the width/height below — so it stays a clean triangle, flush with
            the bubble edge and aligned to the trigger. (The previous `rotate-45`
            square hack sized the SVG to 2.5 but Radix still centered on the
            un-rotated width, so it read as an offset diamond poking out.) Fill is
            an overridable var so a custom-coloured bubble can match its arrow;
            defaults to the grain ink surface. */}
        <TooltipPrimitive.Arrow
          width={11}
          height={6}
          className="z-50 fill-[var(--dash-tooltip-arrow,var(--dash-ink,var(--text-high-emphasis)))]"
        />
      </TooltipPrimitive.Content>
    </TooltipPrimitive.Portal>
  )
}

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider }
